<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { MultiSelectTree } from '@progress/kendo-vue-dropdowns';
import { FilterDescriptor } from '@progress/kendo-data-query';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';
import { InputOptions, BaseInputField } from '@/interfaces/InputFields';
import { processMultiSelectTreeData, expandedState } from '@/lib/multiselecttree-data-operations';

const model = defineModel<any>();
const props = defineProps<
  BaseInputField & {
    autofocus?: boolean;
    disabled?: boolean;
    valid: boolean;
    options: InputOptions[];
  }
>();

const dataItemKey = 'value';
const checkField = 'checkField';
const checkIndeterminateField = 'checkIndeterminateField';
const subItemsField = 'items';
const expandField = 'expanded';
const textField = 'label';
const maxVisibleTags = 3;
const filter = ref<FilterDescriptor>();

const fields = {
  dataItemKey,
  checkField,
  checkIndeterminateField,
  expandField,
  subItemsField,
};

const selectedValues = ref<any[]>([]);
const expanded = ref(props.options?.length ? [props.options[0][dataItemKey]] : []);
const dataItems = ref(props.options);

// Watch for external model changes and update selectedValues
watch(
  () => model.value,
  (newModelValue) => {
    if (Array.isArray(newModelValue)) {
      selectedValues.value = [...newModelValue];
    } else {
      selectedValues.value = [];
    }
  },
  { immediate: true, deep: true }
);

// Watch for options changes and update dataItems
watch(
  () => props.options,
  (newOptions) => {
    dataItems.value = newOptions;
    // Reset expanded if options changed significantly
    if (newOptions?.length && !expanded.value.length) {
      expanded.value = [newOptions[0][dataItemKey]];
    }
  },
  { immediate: true, deep: true }
);

// Handle the "All" selection logic
const handleAllSelection = (newSelectedOptions: InputOptions[]): any[] => {
  const newSelectedValues = newSelectedOptions.map((v) => v.value);
  const currentValues = selectedValues.value;

  const allOption = props.options.find((opt) => opt.value === 'all');
  const allWasSelected = currentValues.includes('all');
  const allIsSelected = newSelectedValues.includes('all');

  // If "All" was just selected
  if (!allWasSelected && allIsSelected) {
    // Select all options
    return props.options.map((opt) => opt.value);
  }

  // If "All" was just deselected
  if (allWasSelected && !allIsSelected) {
    // Clear all selections
    return [];
  }

  // If "All" is currently selected and user deselects any item
  if (allWasSelected && allIsSelected && newSelectedValues.length < props.options.length) {
    // Remove "all" and keep the remaining selections
    return newSelectedValues.filter((v) => v !== 'all');
  }

  // If all options are selected manually, automatically select "All"
  const allNonAllOptionsSelected = newSelectedValues.length === props.options.length - (allOption ? 1 : 0);
  if (allOption && allNonAllOptionsSelected && !allIsSelected) {
    return props.options.map((opt) => opt.value);
  }

  return newSelectedValues;
};

// Computed value for the MultiSelectTree component
const value = computed({
  get() {
    // Return the actual selected options for display
    if (selectedValues.value.includes('all')) {
      return props.options;
    }
    return props.options.filter((opt) => selectedValues.value.includes(opt.value));
  },
  set(newVal: InputOptions[]) {
    const newSelectedValues = handleAllSelection(newVal);
    selectedValues.value = newSelectedValues;
    updateModelValue();
  },
});

const treeData = computed(() =>
  processMultiSelectTreeData(dataItems.value, {
    expanded: expanded.value,
    value: value.value,
    filter: filter.value,
    ...fields,
  })
);

const onExpandChange = (event: any): void => {
  expanded.value = expandedState(event.item, dataItemKey, expanded.value);
};

const onFilterChange = (event: any) => {
  filter.value = event.filter;
};

const updateModelValue = () => {
  model.value = [...selectedValues.value];
};

const onClose = () => {
  updateModelValue();
};

const onChange = (event: any) => {
  if (['delete', 'clear'].includes(event.operation)) {
    // Let the computed setter handle the logic
    // The event should have the new value that will trigger the setter
  }
};

// Additional handler for clear operations
const handleClear = () => {
  selectedValues.value = [];
  updateModelValue();
};
</script>

<template>
  <div class="tw-relative">
    <MultiSelectTree
      :dataItems="treeData"
      v-model="value"
      placeholder="Please select ..."
      :textField="textField"
      :id="name"
      :name="name"
      :disabled="disabled || props.options.length === 0"
      :required="required"
      :autofocus="autofocus"
      :dataItemKey="dataItemKey"
      :checkField="checkField"
      :checkIndeterminateField="checkIndeterminateField"
      :subItemsField="subItemsField"
      :expandField="expandField"
      :checkable="true"
      :filterable="true"
      :auto-close="false"
      @close="onClose"
      @expandchange="onExpandChange"
      @filterchange="onFilterChange"
      @change="onChange"
      class="Field Field--multiselect peer"
      :tag="'tag'"
      :class="{
        'Field--invalid': !valid,
      }"
    >
      <template #header>
        <span v-if="value.length === 0" class="k-placeholder"> Please select... </span>
      </template>
      <template #tag="{ props: tagProps }">
        <template v-if="selectedValues.includes('all')">
          <li class="k-chip" v-if="tagProps.index === 0"> All {{ props.options.length }} items selected. </li>
        </template>
        <template v-else>
          <template v-if="tagProps.index < maxVisibleTags">
            <li class="k-chip">
              {{ tagProps.tagData.text }}
              <span
                class="k-chip-action k-chip-remove-action k-icon k-i-x"
                @click.stop="tagProps.onRemove(tagProps.item)"
              >
                <connx-icon name="fal fa-times" />
              </span>
            </li>
          </template>
          <template v-else-if="tagProps.index === maxVisibleTags">
            <li class="tw-list-none tw-p-2"> +{{ value.length - maxVisibleTags }} selected </li>
          </template>
        </template>
      </template>
    </MultiSelectTree>
  </div>
</template>
